import React, { useState, useEffect, useMemo } from 'react';
import { StudentService, CourseService } from '../services/api';
import Modal from './common/Modal';
import Swal from 'sweetalert2';
import { getTerminology } from '../utils/terminology';
import { useSettings } from '../context/SettingsContext';

import { exportToExcel } from '../utils/exportHelper';
import { QRCodeSVG } from 'qrcode.react';

const StudentList = () => {
  const { settings } = useSettings();
  const [user] = useState(JSON.parse(localStorage.getItem('user')));
  const userRole = user?.role || user?.data?.role;
  const canExport = userRole === 'admin' || userRole === 'super_admin';
  const staffPerms = user?.staff?.permissions || user?.data?.staff?.permissions || [];
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';
  const canAddStudent = isAdmin || staffPerms.includes('add_student');
  const canImportStudent = isAdmin || staffPerms.includes('import_student');

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [viewingStudent, setViewingStudent] = useState(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);

  const handlePrintIdCard = () => {
    const printContent = document.getElementById('id-card-print-area');
    if (!printContent) return;

    let iframe = document.getElementById('print-iframe');
    if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.id = 'print-iframe';
        iframe.style.position = 'absolute';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
    }

    const html = `
      <html>
        <head>
          <title>Print ID Card</title>
          <base href="${window.location.origin}">
          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
          <style>
            @page { size: auto; margin: 0; }
            body { 
              display: flex; 
              justify-content: center; 
              align-items: flex-start;
              padding-top: 20px;
              background-color: white;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .id-card {
              box-shadow: none !important;
              border: 1px solid #e2e8f0 !important;
              page-break-inside: avoid;
            }
            .id-card > .bg-white {
              display: flex;
              flex-direction: column;
              height: 100%;
            }
          </style>
        </head>
        <body>
          ${printContent.outerHTML}
        </body>
      </html>
    `;

    iframe.contentWindow.document.open();
    iframe.contentWindow.document.write(html);
    iframe.contentWindow.document.close();

    setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
    }, 1000);
  };

  const terminology = useMemo(() => {
    return getTerminology(settings.school_type);
  }, [settings.school_type]);

  const studentTerm = terminology.student;
  const studentsTerm = terminology.students;
  const courseTerm = terminology.course;
  const coursesTerm = terminology.courses;
  const batchTerm = terminology.batch;
  const batchesTerm = terminology.batches;

  // View States
  const [viewMode, setViewMode] = useState('active'); // 'active', 'alumni', 'dropped'
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  const [filterCourse, setFilterCourse] = useState('');
  const [filterBatch, setFilterBatch] = useState('');
  const [filterBatches, setFilterBatches] = useState([]);

  // Stats
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, alumni: 0, male: 0, female: 0 });

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    course_id: '',
    batch_id: '',
    status: 'active',
    discount: 0,
    credit_balance: 0,
    gender: 'Male',
    father_name: '',
    mother_name: '',
    religion: 'Islam',
    father_occupation: '',
    father_phone: '',
    admission_date: new Date().toISOString().split('T')[0],
    roll_no: '',
    address: '',
    dob: '',
    cnic: '',
    father_cnic: ''
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  // Import States
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importCourseId, setImportCourseId] = useState('');
  const [importBatchId, setImportBatchId] = useState('');
  const [importBatches, setImportBatches] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const [showExportModal, setShowExportModal] = useState(false);
  const [studentExportFilters, setStudentExportFilters] = useState({
    includeAll: true,
    filter_type: 'all', // 'all', 'course', 'batch'
    course_id: '',
    batch_id: '',
    batches: []
  });

  const [showIdCardModal, setShowIdCardModal] = useState(false);
  const [selectedIdCardStudent, setSelectedIdCardStudent] = useState(null);

  const handleExportCourseChange = (e) => {
    const courseId = e.target.value;
    const selected = courses.find(c => c.id === parseInt(courseId));
    setStudentExportFilters({
      ...studentExportFilters,
      course_id: courseId,
      batch_id: '',
      batches: selected?.batches || []
    });
  };

  useEffect(() => {
    loadStudents();
    loadCourses();
  }, []);

  useEffect(() => {
    if (students.length > 0) {
      calculateStats(students);
    }
  }, [students]);

  const loadStudents = async () => {
    try {
      const response = await StudentService.getAll();
      const data = response.data;
      setStudents(data);
    } catch (error) {
      console.error("Error loading students", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    if (!Array.isArray(data)) return;
    const total = data.length;
    const active = data.filter(s => s.status?.toLowerCase() === 'active').length;
    const inactive = data.filter(s => s.status?.toLowerCase() === 'inactive').length;
    const alumni = data.filter(s => s.status?.toLowerCase() === 'alumni' || s.status?.toLowerCase() === 'graduated').length;
    const male = data.filter(s => s.gender?.toLowerCase() === 'male').length;
    const female = data.filter(s => s.gender?.toLowerCase() === 'female').length;
    setStats({ total, active, inactive, alumni, male, female });
  };

  const loadCourses = async () => {
    try {
      const response = await CourseService.getAll();
      setCourses(response.data);
    } catch (error) {
      console.error("Error loading courses", error);
    }
  };

  const handleFilterCourseChange = (e) => {
    const courseId = e.target.value;
    setFilterCourse(courseId);
    setFilterBatch('');
    if (courseId) {
      const selected = courses.find(c => c.id === parseInt(courseId));
      setFilterBatches(selected?.batches || []);
    } else {
      setFilterBatches([]);
    }
  };

  const handleCourseChange = (e) => {
    const courseId = e.target.value;
    setFormData({ ...formData, course_id: courseId, batch_id: '' });

    if (courseId) {
      const selectedCourse = courses.find(c => c.id === parseInt(courseId));
      if (selectedCourse && selectedCourse.batches) {
        setBatches(selectedCourse.batches);
      } else {
        setBatches([]);
      }
    } else {
      setBatches([]);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });

      if (photoFile) {
        submitData.append('photo', photoFile);
      }

      let res;
      if (editingStudent) {
        res = await StudentService.update(editingStudent.id, submitData);
      } else {
        res = await StudentService.create(submitData);
      }
      const studentResult = res.data;
      const isNewStudent = !editingStudent;
      setShowModal(false);
      setEditingStudent(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      loadStudents();
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        course_id: '',
        batch_id: '',
        status: 'active',
        gender: 'Male',
        father_name: '',
        mother_name: '',
        religion: 'Islam',
        father_occupation: '',
        father_phone: '',
        admission_date: new Date().toISOString().split('T')[0],
        roll_no: '',
        address: '',
        dob: '',
        cnic: '',
        father_cnic: ''
      });

      if (isNewStudent) {
        setTimeout(() => {
          Swal.fire({
            title: 'Admission Successful!',
            text: `Would you like to print the Registration Form for ${studentResult.first_name} ${studentResult.last_name}?`,
            icon: 'success',
            showCancelButton: true,
            confirmButtonColor: '#7367f0',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, Print Now',
            cancelButtonText: 'No, Later'
          }).then((result) => {
            if (result.isConfirmed) {
              handlePrintForm(studentResult);
            }
          });
        }, 300);
      }
    } catch (error) {
      console.error(`Error saving ${studentTerm.toLowerCase()}`, error);
      if (!error.isLimitError) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: `Failed to save ${studentTerm.toLowerCase()}. Please check inputs.`
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleViewIdCard = (student) => {
    setSelectedIdCardStudent(student);
    setShowIdCardModal(true);
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setFormData({
      first_name: student.first_name || '',
      last_name: student.last_name || '',
      email: student.email || '',
      phone: student.phone || '',
      course_id: student.course_id || '',
      batch_id: student.batch_id || '',
      status: student.status || 'active',
      discount: student.discount || 0,
      credit_balance: student.credit_balance || 0,
      gender: student.gender || 'Male',
      father_name: student.father_name || '',
      mother_name: student.mother_name || '',
      religion: student.religion || 'Islam',
      father_occupation: student.father_occupation || '',
      father_phone: student.father_phone || '',
      admission_date: student.admission_date || '',
      roll_no: student.roll_no || '',
      address: student.address || '',
      dob: student.dob || '',
      cnic: student.cnic || '',
      father_cnic: student.father_cnic || ''
    });

    if (student.photo) {
      setPhotoPreview(`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/${student.photo}`);
    } else {
      setPhotoPreview(null);
    }
    setPhotoFile(null);

    if (student.course_id) {
      const selectedCourse = courses.find(c => c.id === parseInt(student.course_id));
      if (selectedCourse && selectedCourse.batches) {
        setBatches(selectedCourse.batches);
      }
    }

    setShowModal(true);
  };

  const handleAddNew = () => {
    setEditingStudent(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      course_id: '',
      batch_id: '',
      status: 'active',
      discount: 0,
      credit_balance: 0,
      gender: 'Male',
      father_name: '',
      mother_name: '',
      religion: 'Islam',
      father_occupation: '',
      father_phone: '',
      admission_date: new Date().toISOString().split('T')[0],
      roll_no: '',
      address: '',
      dob: '',
      cnic: '',
      father_cnic: ''
    });
    setPhotoPreview(null);
    setPhotoFile(null);
    setShowModal(true);
  };

  const handleImportClick = () => {
    setImportResult(null);
    setImportFile(null);
    setImportCourseId('');
    setImportBatchId('');
    setShowImportModal(true);
  };

  const handleImportCourseChange = (e) => {
    const cid = e.target.value;
    setImportCourseId(cid);
    const selectedCourse = courses.find(c => c.id === parseInt(cid));
    setImportBatches(selectedCourse ? selectedCourse.batches : []);
    setImportBatchId('');
  };

  const handleImportSubmit = async (e) => {
    e.preventDefault();
    if (!importFile) return;

    setImporting(true);
    try {
      const data = new FormData();
      data.append('file', importFile);
      data.append('course_id', importCourseId);
      data.append('batch_id', importBatchId);

      const response = await StudentService.importStudents(data);
      setImportResult(response.data);
      loadStudents();

      if (response.data.success > 0 && response.data.errors.length === 0) {
        Swal.fire({
          icon: 'success',
          title: 'Import Successful',
          text: `Successfully imported ${response.data.success} students.`,
          timer: 2000
        });
        setShowImportModal(false);
      }
    } catch (error) {
      console.error("Import failed", error);
      Swal.fire({ icon: 'error', title: 'Import Failed', text: 'Something went wrong during import.' });
    } finally {
      setImporting(false);
    }
  };

  const downloadSampleCSV = () => {
    const headers = ['first_name', 'last_name', 'gender', 'phone', 'father_name', 'mother_name', 'religion', 'father_occupation', 'father_phone', 'email', 'cnic', 'father_cnic', 'dob', 'address', 'roll_no', 'discount', 'credit_balance'];
    const row1 = ['John', 'Doe', 'Male', '03001234567', 'Richard Doe', 'Jane Doe', 'Islam', 'Business', '03001234568', 'john@example.com', '42101-1234567-1', '42101-1234567-2', '2000-01-01', '123 Main St', '101', '0', '0'];
    const csvContent = [headers, row1].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "student_import_sample.csv");
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleView = (student) => {
    setViewingStudent(student);
    setShowViewModal(true);
  };
  
  const handlePrintForm = (student) => {
    setViewingStudent(student);
    setShowRegistrationModal(true);
  };

  const handleViewReport = async (student) => {
    try {
      setLoading(true);
      const res = await StudentService.get(student.id);
      setReportData(res.data);
      setShowReportModal(true);
    } catch (error) {
      console.error("Error loading report card", error);
      Swal.fire('Error', 'Failed to load report card data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id, name) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${name}. This will also delete all associated data (attendance, fees, exam results).`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await StudentService.delete(id);
          Swal.fire(
            'Deleted!',
            'Student record has been deleted.',
            'success'
          );
          loadStudents();
        } catch (error) {
          console.error("Error deleting student", error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to delete student record.'
          });
        }
      }
    });
  };

  const handleExport = () => {
    setShowExportModal(true);
  };

  const performExport = () => {
    let dataToExport = students;

    if (!studentExportFilters.includeAll) {
      dataToExport = students.filter(student => {
        const matchesCourse = studentExportFilters.filter_type === 'all' || 
          String(student.course_id) === String(studentExportFilters.course_id);
        
        const matchesBatch = studentExportFilters.filter_type !== 'batch' || 
          String(student.batch_id) === String(studentExportFilters.batch_id);

        return matchesCourse && matchesBatch;
      });
    }

    if (dataToExport.length === 0) {
      Swal.fire('No Data', 'No students found for the selected filters.', 'info');
      return;
    }

    const exportData = dataToExport.map(student => ({
      'Registration No': student.reg_no || student.id,
      'First Name': student.first_name,
      'Last Name': student.last_name,
      'Father Name': student.father_name || '-',
      'Gender': student.gender || '-',
      'Phone': student.phone || '-',
      'Email': student.email || '-',
      'CNIC': student.cnic || '-',
      [courseTerm]: student.course?.name || '-',
      [batchTerm]: student.batch?.name || '-',
      'Roll Number': student.roll_number || '-',
      'Admission Date': student.admission_date || '-',
      'Status': (student.status || 'Active').toUpperCase(),
      'Address': student.address || '-'
    }));

    exportToExcel(exportData, `${studentsTerm}_Export_${new Date().toISOString().split('T')[0]}`);
    setShowExportModal(false);
  };


  const filteredStudents = useMemo(() => {
    return students.filter(student => {
      // Status Filter
      if (viewMode === 'active' && student.status !== 'active') return false;
      if (viewMode === 'inactive' && student.status !== 'inactive') return false;
      if (viewMode === 'alumni' && student.status !== 'alumni' && student.status !== 'graduated') return false;
      if (viewMode === 'dropped' && student.status !== 'dropped') return false;

      // Course Filter
      if (filterCourse && String(student.course_id) !== String(filterCourse)) return false;

      // Batch Filter
      if (filterBatch && String(student.batch_id) !== String(filterBatch)) return false;

      // Search Filter
      const searchTermLower = searchTerm.toLowerCase();
      const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
      return (
        fullName.includes(searchTermLower) ||
        student.email?.toLowerCase().includes(searchTermLower) ||
        student.reg_no?.toLowerCase().includes(searchTermLower) ||
        student.phone?.includes(searchTermLower)
      );
    });
  }, [students, viewMode, searchTerm, filterCourse, filterBatch]);

  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const currentData = filteredStudents.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [viewMode, searchTerm, filterCourse, filterBatch]);

  return (
    <div className="container-fluid p-0">
      <div className="section-header mb-4 d-flex justify-content-between align-items-start flex-wrap-mobile">
        <div>
          <h2 className="fw-bold">{studentTerm} Management</h2>
          <p className="text-muted">Manage admissions, {studentsTerm.toLowerCase()}, and alumni</p>
        </div>
        <div className="d-flex gap-2 flex-wrap-mobile w-100-mobile">
          {canExport && (
            <button
              className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm"
              onClick={handleExport}
            >
              <i className="bi bi-file-earmark-spreadsheet"></i> Export
            </button>
          )}
          {canImportStudent && (
            <button
              className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm"
              onClick={handleImportClick}
            >
              <i className="bi bi-file-earmark-arrow-up"></i> Import
            </button>
          )}
          {canAddStudent && (
            <button
              className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm"
              onClick={handleAddNew}
            >
              <i className="bi bi-plus-lg"></i> Add {studentTerm}
            </button>
          )}
        </div>
      </div>

      <div className="stats-grid mb-5">
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Total {studentsTerm}</p>
            <h3 className="mb-0">{stats.total}</h3>
          </div>
          <div className="stat-icon bg-purple-light text-primary">
            <i className="bi bi-people-fill"></i>
          </div>
        </div>
        <div
          className={`stat-card cursor-pointer ${viewMode === 'active' ? 'border-primary' : ''}`}
          onClick={() => setViewMode('active')}
        >
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Active {studentsTerm}</p>
            <h3 className="mb-0">{stats.active}</h3>
          </div>
          <div className="stat-icon bg-green-light">
            <i className="bi bi-person-check-fill"></i>
          </div>
        </div>
        <div
          className={`stat-card cursor-pointer ${viewMode === 'alumni' ? 'border-primary' : ''}`}
          onClick={() => setViewMode('alumni')}
        >
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Alumni</p>
            <h3 className="mb-0">{stats.alumni}</h3>
          </div>
          <div className="stat-icon bg-orange-light">
            <i className="bi bi-mortarboard-fill"></i>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1 small fw-bold">Gender Ratio</p>
            <h3 className="mb-0">{stats.male}M / {stats.female}F</h3>
          </div>
          <div className="stat-icon bg-info-light text-info">
            <i className="bi bi-gender-ambiguous"></i>
          </div>
        </div>
      </div>

      <div className="card-box shadow-sm border">
        <div className="section-header px-4 d-flex justify-content-between align-items-center mb-3 flex-wrap-mobile">
          <div className="d-flex align-items-center gap-3">
            <h5 className="mb-0 fw-bold">{studentTerm} List</h5>
            <div className="bg-light p-1 rounded-pill d-flex overflow-auto">
              <button
                className={`btn btn-sm rounded-pill px-3 ${viewMode === 'active' ? 'btn-white shadow-sm fw-bold text-success' : 'text-muted'}`}
                onClick={() => setViewMode('active')}
              >
                Active
              </button>
              <button
                className={`btn btn-sm rounded-pill px-3 ${viewMode === 'inactive' ? 'btn-white shadow-sm fw-bold text-secondary' : 'text-muted'}`}
                onClick={() => setViewMode('inactive')}
              >
                Inactive
              </button>
              <button
                className={`btn btn-sm rounded-pill px-3 ${viewMode === 'alumni' ? 'btn-white shadow-sm fw-bold text-warning' : 'text-muted'}`}
                onClick={() => setViewMode('alumni')}
              >
                Alumni
              </button>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap-mobile">
            <div className="position-relative" style={{ width: '250px' }}>
              <i className="bi bi-search position-absolute text-muted" style={{ left: '15px', top: '50%', transform: 'translateY(-50%)' }}></i>
              <input
                type="text"
                className="form-control rounded-pill ps-5 bg-light border-0"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ height: '40px' }}
              />
            </div>

            <select
              className="form-select form-select-sm rounded-pill border-0 shadow-sm bg-light px-3"
              style={{ width: '150px', height: '40px' }}
              value={filterCourse}
              onChange={handleFilterCourseChange}
            >
              <option value="">All {coursesTerm}</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>

            <select
              className="form-select form-select-sm rounded-pill border-0 shadow-sm bg-light px-3"
              style={{ width: '150px', height: '40px' }}
              value={filterBatch}
              onChange={(e) => setFilterBatch(e.target.value)}
              disabled={!filterCourse}
            >
              <option value="">All {batchesTerm}</option>
              {filterBatches.map(batch => (
                <option key={batch.id} value={batch.id}>{batch.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th className="ps-4">{studentTerm} Name</th>
                <th>{courseTerm} / {batchTerm}</th>
                <th>Contact</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="text-center py-5 text-muted">Loading {studentsTerm.toLowerCase()} records...</td></tr>
              ) : currentData.length > 0 ? (
                currentData.map((student) => (
                  <tr key={student.id}>
                    <td className="ps-4">
                      <div className="d-flex align-items-center gap-3">
                        {student.photo ? (
                          <img
                          src={`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}${student.photo.startsWith('/') ? '' : '/'}${student.photo}`}
                            alt={student.first_name}
                            className="rounded-circle shadow-sm"
                            style={{ width: 42, height: 42, objectFit: 'cover' }}
                          />
                        ) : (
                          <div
                            className={`avatar-sm text-white d-flex align-items-center justify-content-center rounded-circle fw-bold shadow-sm`}
                            style={{ width: 42, height: 42, fontSize: '1.2rem', background: '#7367F0' }}
                          >
                            {student.first_name.charAt(0)}
                          </div>
                        )}
                        <div>
                          <div className="fw-bold text-dark">{student.first_name} {student.last_name}</div>
                          <small className="text-muted">ID: <span className="fw-bold">{student.reg_no || `#${student.id}`}</span></small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-column">
                        <span className="fw-semibold text-primary">{student.course?.name}</span>
                        <span className="small text-muted">{student.batch?.name || `No ${batchTerm}`}</span>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-column">
                        <span className="text-dark"><i className="bi bi-telephone me-1 text-muted"></i>{student.phone}</span>
                        <span className="small text-muted"><i className="bi bi-envelope me-1"></i>{student.email}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`badge rounded-pill fw-normal px-3 py-2 ${student.status === 'active' ? 'bg-success-subtle text-success border border-success' : 'bg-secondary-subtle text-secondary border'}`}>
                        {student.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center gap-2">
                        <button
                          className="btn btn-icon btn-primary rounded-circle shadow-sm d-flex align-items-center justify-content-center p-0 text-white"
                          style={{ width: 35, height: 35 }}
                          title="Print Registration Form"
                          onClick={() => handlePrintForm(student)}
                        >
                          <i className="bi bi-printer-fill"></i>
                        </button>
                        <button
                          className="btn btn-icon btn-light rounded-circle text-info shadow-sm d-flex align-items-center justify-content-center p-0"
                          style={{ width: 35, height: 35 }}
                          title="View Details"
                          onClick={() => handleView(student)}
                        >
                          <i className="bi bi-eye-fill"></i>
                        </button>
                        <button
                          className="btn btn-icon btn-light rounded-circle text-success shadow-sm d-flex align-items-center justify-content-center p-0"
                          style={{ width: 35, height: 35 }}
                          title="Report Card"
                          onClick={() => handleViewReport(student)}
                        >
                          <i className="bi bi-award-fill"></i>
                        </button>
                        {isAdmin && (
                          <>
                            <button
                              className="btn btn-icon btn-light rounded-circle text-primary shadow-sm d-flex align-items-center justify-content-center p-0"
                              style={{ width: 35, height: 35 }}
                              title={`ID Card`}
                              onClick={() => handleViewIdCard(student)}
                            >
                              <i className="bi bi-person-badge-fill"></i>
                            </button>
                            <button
                              className="btn btn-icon btn-light rounded-circle text-warning shadow-sm d-flex align-items-center justify-content-center p-0"
                              style={{ width: 35, height: 35 }}
                              title={`Edit ${studentTerm}`}
                              onClick={() => handleEdit(student)}
                            >
                              <i className="bi bi-pencil-fill"></i>
                            </button>
                            <button
                              className="btn btn-icon btn-light rounded-circle text-danger shadow-sm d-flex align-items-center justify-content-center p-0"
                              style={{ width: 35, height: 35 }}
                              title={`Delete ${studentTerm}`}
                              onClick={() => handleDelete(student.id, `${student.first_name} ${student.last_name}`)}
                            >
                              <i className="bi bi-trash-fill"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-5">
                    <div className="d-flex flex-column align-items-center">
                      <i className="bi bi-search text-muted display-6 mb-3"></i>
                      <p className="text-muted fw-semibold">No {studentsTerm.toLowerCase()} found matching your search.</p>
                      <button className="btn btn-sm btn-outline-primary rounded-pill" onClick={() => setSearchTerm('')}>Clear Search</button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {
          totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center px-4 py-3 border-top">
              <small className="text-muted">Showing page {currentPage} of {totalPages}</small>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-light btn-sm rounded-circle shadow-sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => prev - 1)}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>

                <div className="d-flex align-items-center gap-1 mx-2">
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    // Show current page, first, last, and pages immediately surrounding current
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNumber}
                          className={`btn btn-sm ${currentPage === pageNumber ? 'btn-primary' : 'btn-light text-muted'} text-center`}
                          style={{ width: '30px', height: '30px', padding: 0, borderRadius: '50%' }}
                          onClick={() => setCurrentPage(pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      );
                    } else if (
                      pageNumber === currentPage - 2 ||
                      pageNumber === currentPage + 2
                    ) {
                      return <span key={pageNumber} className="text-muted small">...</span>;
                    }
                    return null;
                  })}
                </div>

                <button
                  className="btn btn-light btn-sm rounded-circle shadow-sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          )
        }
      </div>

      <Modal
        show={showModal}
        size="modal-xl"
        onClose={() => {
          setShowModal(false);
          setEditingStudent(null);
        }}
        title={editingStudent ? `Edit ${studentTerm}` : `Add New ${studentTerm}`}
      >
        <form onSubmit={handleSubmit}>
          <div className="mb-4 text-center">
            <label className="form-label small text-muted fw-bold d-block mb-3">{studentTerm} Photo</label>
            <div className="d-flex justify-content-center">
              <div className="position-relative" style={{ width: 120, height: 120 }}>
                <div
                  className="rounded-circle border border-3 border-primary shadow-sm overflow-hidden bg-light d-flex align-items-center justify-content-center"
                  style={{ width: '100%', height: '100%' }}
                >
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt="Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <i className="bi bi-person-circle text-muted" style={{ fontSize: '4rem' }}></i>
                  )}
                </div>

                <label
                  htmlFor="photoInput"
                  className="position-absolute bg-primary text-white rounded-circle d-flex align-items-center justify-content-center shadow"
                  style={{
                    width: 36,
                    height: 36,
                    bottom: 0,
                    right: 0,
                    cursor: 'pointer',
                    border: '3px solid white'
                  }}
                  title="Upload Photo"
                >
                  <i className="bi bi-camera-fill"></i>
                </label>

                {photoPreview && (
                  <button
                    type="button"
                    onClick={() => {
                      setPhotoFile(null);
                      setPhotoPreview(null);
                    }}
                    className="position-absolute bg-danger text-white rounded-circle d-flex align-items-center justify-content-center shadow border-0"
                    style={{
                      width: 36,
                      height: 36,
                      top: 0,
                      right: 0,
                      border: '3px solid white'
                    }}
                    title="Remove Photo"
                  >
                    <i className="bi bi-trash-fill"></i>
                  </button>
                )}

                <input
                  id="photoInput"
                  type="file"
                  className="d-none"
                  accept="image/*"
                  onChange={handlePhotoChange}
                />
              </div>
            </div>
            <small className="text-muted d-block mt-2">JPG, PNG, GIF (Max 2MB)</small>
          </div>

          <div className="row">
            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">First Name</label>
              <input type="text" className="form-control" name="first_name" value={formData.first_name} onChange={handleInputChange} required />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">Last Name</label>
              <input type="text" className="form-control" name="last_name" value={formData.last_name} onChange={handleInputChange} required />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">Gender</label>
              <select className="form-select" name="gender" value={formData.gender} onChange={handleInputChange}>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">Date of Birth</label>
              <input type="date" className="form-control" name="dob" value={formData.dob} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">Student CNIC / B-Form</label>
              <input type="text" className="form-control" name="cnic" value={formData.cnic} onChange={handleInputChange} placeholder="XXXXX-XXXXXXX-X" />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">Father's Name</label>
              <input type="text" className="form-control" name="father_name" value={formData.father_name} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">Father's CNIC</label>
              <input type="text" className="form-control" name="father_cnic" value={formData.father_cnic} onChange={handleInputChange} placeholder="XXXXX-XXXXXXX-X" />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">Mother's Name</label>
              <input type="text" className="form-control" name="mother_name" value={formData.mother_name} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">Religion</label>
              <input type="text" className="form-control" name="religion" value={formData.religion} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">Father's Occupation</label>
              <input type="text" className="form-control" name="father_occupation" value={formData.father_occupation} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">Father's Phone</label>
              <input type="text" className="form-control" name="father_phone" value={formData.father_phone} onChange={handleInputChange} />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">Admission Date</label>
              <input type="date" className="form-control" name="admission_date" value={formData.admission_date} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">Email Address</label>
              <input type="email" className="form-control" name="email" value={formData.email} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">Phone Number</label>
              <input type="text" className="form-control" name="phone" value={formData.phone} onChange={handleInputChange} required />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">{courseTerm}</label>
              <select className="form-select" name="course_id" value={formData.course_id} onChange={handleCourseChange} required>
                <option value="">Select {courseTerm}</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id}>{course.name}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">{batchTerm}</label>
              <select className="form-select" name="batch_id" value={formData.batch_id} onChange={handleInputChange} required disabled={!formData.course_id}>
                <option value="">Select {batchTerm}</option>
                {batches.map(batch => (
                  <option key={batch.id} value={batch.id}>{batch.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">Status</label>
              <select className="form-select" name="status" value={formData.status} onChange={handleInputChange} required>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="alumni">Alumni</option>
              </select>
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">Discount (Flat Amount)</label>
              <input type="number" className="form-control" name="discount" value={formData.discount} onChange={handleInputChange} min="0" />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label small text-muted fw-bold">Credit Balance</label>
              <input type="number" className="form-control" name="credit_balance" value={formData.credit_balance} onChange={handleInputChange} min="0" />
            </div>
            <div className="col-md-12 mb-3">
              <label className="form-label small text-muted fw-bold">Address</label>
              <textarea className="form-control" name="address" value={formData.address} onChange={handleInputChange} rows="1"></textarea>
            </div>
          </div>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => {
              setShowModal(false);
              setEditingStudent(null);
            }}>Cancel</button>
            <button type="submit" className="btn btn-primary px-4 rounded-pill d-flex align-items-center gap-2" disabled={saving}>
              {saving && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
              {saving ? 'Saving...' : (editingStudent ? `Update ${studentTerm}` : `Save ${studentTerm}`)}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        show={showViewModal}
        size="modal-lg"
        onClose={() => {
          setShowViewModal(false);
          setViewingStudent(null);
        }}
        title={`${studentTerm} Details`}
      >
        {viewingStudent && (
          <div className="student-details">
            <div className="text-center mb-4">
              {viewingStudent.photo ? (
                <img
                  src={viewingStudent.photo.startsWith('http') ? viewingStudent.photo : `${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}${viewingStudent.photo.startsWith('/') ? '' : '/'}${viewingStudent.photo}`}
                  alt={viewingStudent.first_name}
                  className="rounded-circle shadow border"
                  style={{ width: 100, height: 100, objectFit: 'cover' }}
                />
              ) : (
                <div
                  className="avatar-lg text-white d-inline-flex align-items-center justify-content-center rounded-circle fw-bold shadow"
                  style={{ width: 80, height: 80, fontSize: '2rem', background: '#7367F0' }}
                >
                  {viewingStudent.first_name.charAt(0)}
                </div>
              )}
              <h4 className="mt-3 mb-1">{viewingStudent.first_name} {viewingStudent.last_name}</h4>
              <span className={`badge rounded-pill ${viewingStudent.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                {viewingStudent.status.toUpperCase()}
              </span>
            </div>

            <div className="row g-3">
              <div className="col-12 border-bottom pb-1 mb-1">
                <h6 className="fw-bold text-primary mb-0 small"><i className="bi bi-person-badge me-2"></i>Personal & Academic Information</h6>
              </div>
              <div className="col-md-3">
                <label className="form-label mb-0 small text-muted fw-bold">Gender</label>
                <p className="fw-semibold mb-2">{viewingStudent.gender || 'N/A'}</p>
              </div>
              <div className="col-md-3">
                <label className="form-label mb-0 small text-muted fw-bold">Date of Birth</label>
                <p className="fw-semibold mb-2">{viewingStudent.dob || 'N/A'}</p>
              </div>
              <div className="col-md-3">
                <label className="form-label mb-0 small text-muted fw-bold">Student CNIC</label>
                <p className="fw-semibold mb-2">{viewingStudent.cnic || 'N/A'}</p>
              </div>
              <div className="col-md-3">
                <label className="form-label mb-0 small text-muted fw-bold">Religion</label>
                <p className="fw-semibold mb-2">{viewingStudent.religion || 'N/A'}</p>
              </div>
              <div className="col-md-3">
                <label className="form-label mb-0 small text-muted fw-bold">Admission Date</label>
                <p className="fw-semibold mb-2">{viewingStudent.admission_date || 'N/A'}</p>
              </div>

              <div className="col-md-3">
                <label className="form-label mb-0 small text-muted fw-bold">{courseTerm}</label>
                <p className="fw-semibold mb-2 text-primary">{viewingStudent.course?.name || 'N/A'}</p>
              </div>
              <div className="col-md-3">
                <label className="form-label mb-0 small text-muted fw-bold">{batchTerm}</label>
                <p className="fw-semibold mb-2">{viewingStudent.batch?.name || `No ${batchTerm}`}</p>
              </div>
              <div className="col-md-3">
                <label className="form-label mb-0 small text-muted fw-bold">Status</label>
                <p className="fw-semibold mb-2">{viewingStudent.status.toUpperCase()}</p>
              </div>
              <div className="col-md-3">
                <label className="form-label mb-0 small text-muted fw-bold">Roll No</label>
                <p className="fw-semibold mb-2">{viewingStudent.roll_no || 'N/A'}</p>
              </div>
              <div className="col-md-3">
                <label className="form-label mb-0 small text-muted fw-bold">Discount (Flat)</label>
                <p className="fw-semibold mb-2 text-danger">Rs. {viewingStudent.discount || 0}</p>
              </div>
              <div className="col-md-3">
                <label className="form-label mb-0 small text-muted fw-bold">Credit Balance</label>
                <p className="fw-semibold mb-2 text-success">Rs. {viewingStudent.credit_balance || 0}</p>
              </div>

              <div className="col-12 border-bottom pb-1 my-1">
                <h6 className="fw-bold text-primary mb-0 small"><i className="bi bi-people me-2"></i>Guardian & Contact Information</h6>
              </div>
              <div className="col-md-3">
                <label className="form-label mb-0 small text-muted fw-bold">Father's Name</label>
                <p className="fw-semibold mb-2">{viewingStudent.father_name || 'N/A'}</p>
              </div>
              <div className="col-md-3">
                <label className="form-label mb-0 small text-muted fw-bold">Father's CNIC</label>
                <p className="fw-semibold mb-2">{viewingStudent.father_cnic || 'N/A'}</p>
              </div>
              <div className="col-md-3">
                <label className="form-label mb-0 small text-muted fw-bold">Mother's Name</label>
                <p className="fw-semibold mb-2">{viewingStudent.mother_name || 'N/A'}</p>
              </div>
              <div className="col-md-3">
                <label className="form-label mb-0 small text-muted fw-bold">Father's Occupation</label>
                <p className="fw-semibold mb-2">{viewingStudent.father_occupation || 'N/A'}</p>
              </div>
              <div className="col-md-3">
                <label className="form-label mb-0 small text-muted fw-bold">Father's Phone</label>
                <p className="fw-semibold mb-2">{viewingStudent.father_phone || 'N/A'}</p>
              </div>

              <div className="col-md-3">
                <label className="form-label mb-0 small text-muted fw-bold">Student Phone</label>
                <p className="fw-semibold mb-2">{viewingStudent.phone}</p>
              </div>
              <div className="col-md-3">
                <label className="form-label mb-0 small text-muted fw-bold">Email</label>
                <p className="fw-semibold mb-2 text-truncate">{viewingStudent.email || 'N/A'}</p>
              </div>
              <div className="col-md-6">
                <label className="form-label mb-0 small text-muted fw-bold">Address</label>
                <p className="fw-semibold mb-0 small">{viewingStudent.address || 'N/A'}</p>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                className="btn btn-light px-4 rounded-pill"
                onClick={() => {
                  setShowViewModal(false);
                  setViewingStudent(null);
                }}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary px-4 rounded-pill"
                onClick={() => {
                  setShowViewModal(false);
                  handleEdit(viewingStudent);
                }}
              >
                <i className="bi bi-pencil me-2"></i>Edit {studentTerm}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal show={showExportModal} onClose={() => setShowExportModal(false)} title={`Export ${studentsTerm} Data to Excel`}>
        <div className="p-2">
          <div className="mb-4">
            <div className="form-check form-switch mb-3">
              <input 
                className="form-check-input" 
                type="checkbox" 
                id="exportAllStudents" 
                checked={studentExportFilters.includeAll}
                onChange={(e) => setStudentExportFilters({...studentExportFilters, includeAll: e.target.checked})}
              />
              <label className="form-check-label fw-bold" htmlFor="exportAllStudents">Export All {studentsTerm} (Ignore Filters)</label>
            </div>
            
            {!studentExportFilters.includeAll && (
              <div className="animate__animated animate__fadeIn">
                <label className="form-label small fw-bold mb-2">Scope Options:</label>
                <div className="d-flex gap-4 mb-3">
                  <div className="form-check">
                    <input className="form-check-input" type="radio" name="exportScope" id="scopeAll" checked={studentExportFilters.filter_type === 'all'} onChange={() => setStudentExportFilters({...studentExportFilters, filter_type: 'all'})} />
                    <label className="form-check-label small" htmlFor="scopeAll">All Registered</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="radio" name="exportScope" id="scopeClass" checked={studentExportFilters.filter_type === 'course'} onChange={() => setStudentExportFilters({...studentExportFilters, filter_type: 'course'})} />
                    <label className="form-check-label small" htmlFor="scopeClass">By {courseTerm}</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="radio" name="exportScope" id="scopeSection" checked={studentExportFilters.filter_type === 'batch'} onChange={() => setStudentExportFilters({...studentExportFilters, filter_type: 'batch'})} />
                    <label className="form-check-label small" htmlFor="scopeSection">By {batchTerm}</label>
                  </div>
                </div>

                {studentExportFilters.filter_type !== 'all' && (
                  <div className="row g-2 mb-2">
                    <div className={studentExportFilters.filter_type === 'batch' ? 'col-md-6' : 'col-md-12'}>
                       <label className="form-label small text-muted fw-bold">{courseTerm}</label>
                       <select className="form-select form-select-sm" value={studentExportFilters.course_id} onChange={handleExportCourseChange}>
                         <option value="">Select {courseTerm}...</option>
                         {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                       </select>
                    </div>
                    {studentExportFilters.filter_type === 'batch' && (
                       <div className="col-md-6">
                          <label className="form-label small text-muted fw-bold">{batchTerm}</label>
                          <select className="form-select form-select-sm" value={studentExportFilters.batch_id} onChange={(e) => setStudentExportFilters({...studentExportFilters, batch_id: e.target.value})}>
                            <option value="">Select {batchTerm}...</option>
                            {studentExportFilters.batches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                          </select>
                       </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
            <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowExportModal(false)}>Cancel</button>
            <button type="button" className="btn btn-success px-4 rounded-pill" onClick={performExport}>
              <i className="bi bi-file-earmark-excel me-2"></i> Export Now
            </button>
          </div>
        </div>
      </Modal>


      {/* Report Card Modal */}
      <Modal
        show={showReportModal}
        size="modal-lg"
        onClose={() => setShowReportModal(false)}
        title={`Student Report Card: ${reportData?.first_name} ${reportData?.last_name}`}
      >
        {reportData && (
          <div className="report-card p-3">
            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
              <div>
                <h4 className="fw-bold text-primary mb-1">{reportData.first_name} {reportData.last_name}</h4>
                <p className="text-muted mb-0 small">
                  Roll No: <strong>{reportData.roll_no || 'N/A'}</strong> |&nbsp;
                  {courseTerm}: <strong>{reportData.course?.name}</strong> |&nbsp;
                  {batchTerm}: <strong>{reportData.batch?.name}</strong>
                </p>
              </div>
              <div className="text-end">
                <span className={`badge rounded-pill px-3 py-2 ${reportData.status === 'active' ? 'bg-success-subtle text-success border border-success' : 'bg-secondary-subtle text-secondary border'}`}>
                  {reportData.status.toUpperCase()}
                </span>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="bg-light">
                  <tr>
                    <th>Exam / Book Name</th>
                    <th className="text-center">Total Marks</th>
                    <th className="text-center">Obtained</th>
                    <th className="text-center">Percentage</th>
                    <th className="text-center">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.exam_results && reportData.exam_results.length > 0 ? (
                    reportData.exam_results.map((res) => {
                      const percentage = res.exam?.total_marks > 0
                        ? ((res.obtained_marks / res.exam.total_marks) * 100).toFixed(1)
                        : 0;
                      const passingMarks = settings.passing_percentage || 40;
                      const isPassed = percentage >= passingMarks;

                      return (
                        <tr key={res.id}>
                          <td>
                            <div className="fw-bold">{res.exam?.name || 'N/A'}</div>
                            <small className="text-muted">{res.exam?.exam_date ? new Date(res.exam.exam_date).toLocaleDateString() : 'N/A'}</small>
                          </td>
                          <td className="text-center">{res.exam?.total_marks}</td>
                          <td className="text-center fw-bold">{res.obtained_marks}</td>
                          <td className="text-center">
                            <span className="fw-bold">{percentage}%</span>
                            <div className="progress mx-auto" style={{ height: '4px', width: '60px' }}>
                              <div
                                className={`progress-bar ${isPassed ? 'bg-success' : 'bg-danger'}`}
                                role="progressbar"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </td>
                          <td className="text-center">
                            <span className={`badge rounded-pill px-3 py-1 ${isPassed ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                              {isPassed ? 'PASS' : 'FAIL'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-4 text-muted small">
                        No exam results found for this {studentTerm.toLowerCase()}.
                      </td>
                    </tr>
                  )}
                </tbody>
                {reportData.exam_results && reportData.exam_results.length > 0 && (
                  <tfoot className="bg-light fw-bold">
                    <tr>
                      <td>Overall Summary</td>
                      <td className="text-center">
                        {reportData.exam_results.reduce((acc, res) => acc + (res.exam?.total_marks || 0), 0)}
                      </td>
                      <td className="text-center">
                        {reportData.exam_results.reduce((acc, res) => acc + (res.obtained_marks || 0), 0)}
                      </td>
                      <td className="text-center">
                        {(reportData.exam_results.reduce((acc, res) => acc + parseFloat(res.exam?.total_marks > 0 ? ((res.obtained_marks / res.exam.total_marks) * 100) : 0), 0) / reportData.exam_results.length).toFixed(1)}%
                      </td>
                      <td className="text-center">
                        {reportData.exam_results.every(res => ((res.obtained_marks / (res.exam?.total_marks || 1)) * 100) >= (settings.passing_percentage || 40))
                          ? <span className="text-success">PASS</span>
                          : <span className="text-danger">FAIL</span>}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            <div className="mt-4 text-end">
              <button className="btn btn-light px-4 rounded-pill shadow-sm me-2" onClick={() => window.print()}>
                <i className="bi bi-printer me-2"></i>Print Report
              </button>
              <button className="btn btn-primary px-4 rounded-pill shadow-sm" onClick={() => setShowReportModal(false)}>
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Import Students Modal */}
      <Modal
        show={showImportModal}
        onClose={() => setShowImportModal(false)}
        title={`Import ${studentsTerm}`}
        size="modal-lg"
      >
        <form onSubmit={handleImportSubmit}>
          <div className="alert alert-info border-0 rounded-4 p-4 mb-4">
            <h6 className="fw-bold mb-2"><i className="bi bi-info-circle-fill me-2"></i> Instructions</h6>
            <ul className="small mb-0 ps-3">
              <li>Download the sample CSV file to see the required format.</li>
              <li>Columns must be: <strong>first_name, last_name, gender, phone, father_name, mother_name, religion, father_occupation, father_phone, email, cnic, father_cnic, dob, address, roll_no, discount, credit_balance</strong>.</li>
              <li>Headers must match exactly (case-insensitive).</li>
              <li>Gender should be <strong>Male</strong> or <strong>Female</strong>.</li>
              <li>Email must be unique if provided.</li>
              <li>Required fields: <strong>first_name, last_name, phone</strong>.</li>
            </ul>
            <button
              type="button"
              className="btn btn-link btn-sm p-0 mt-3 text-decoration-none fw-bold"
              onClick={downloadSampleCSV}
            >
              <i className="bi bi-download me-2"></i> Download Sample CSV
            </button>
          </div>

          <div className="row g-4 mb-4">
            <div className="col-md-6">
              <label className="form-label small fw-bold">Select {courseTerm} *</label>
              <select
                className="form-select rounded-4 py-2"
                required
                value={importCourseId}
                onChange={handleImportCourseChange}
              >
                <option value="">Choose {courseTerm}...</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id}>{course.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-bold">Select {batchTerm} *</label>
              <select
                className="form-select rounded-4 py-2"
                required
                value={importBatchId}
                onChange={(e) => setImportBatchId(e.target.value)}
                disabled={!importCourseId}
              >
                <option value="">Choose {batchTerm}...</option>
                {importBatches.map(batch => (
                  <option key={batch.id} value={batch.id}>{batch.name}</option>
                ))}
              </select>
            </div>
            <div className="col-12">
              <label className="form-label small fw-bold">Choose CSV File *</label>
              <input
                type="file"
                className="form-control rounded-4 py-2"
                accept=".csv"
                required
                onChange={(e) => setImportFile(e.target.files[0])}
              />
            </div>
          </div>

          {importResult && (
            <div className={`alert ${importResult.errors.length > 0 ? 'alert-warning' : 'alert-success'} border-0 rounded-4 p-4 mb-4`}>
              <h6 className="fw-bold mb-3">Import Results:</h6>
              <p className="mb-2 text-success"><i className="bi bi-check-circle-fill me-2"></i> Successfully imported: <strong>{importResult.success}</strong></p>
              {importResult.errors.length > 0 && (
                <div className="mt-3">
                  <p className="text-danger mb-2 font-fixed small"><i className="bi bi-exclamation-triangle-fill me-2"></i> Errors encountered in <strong>{importResult.errors.length}</strong> rows:</p>
                  <div className="overflow-auto bg-white rounded-3 p-3 border" style={{ maxHeight: '150px' }}>
                    {importResult.errors.map((err, idx) => (
                      <div key={idx} className="small text-muted mb-2 border-bottom pb-1">
                        <span className="fw-bold text-dark">Row {err.row}:</span> {err.errors.join(", ")}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="text-end">
            <button
              type="button"
              className="btn btn-light rounded-pill px-4 me-2"
              onClick={() => setShowImportModal(false)}
            >
              Close
            </button>
            <button
              type="submit"
              className="btn btn-primary rounded-pill px-4"
              disabled={importing || !importFile || !importBatchId}
            >
              {importing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Importing...
                </>
              ) : 'Start Import'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Registration Form Print Modal */}
      <Modal show={showRegistrationModal} onClose={() => setShowRegistrationModal(false)} title="Registration Form"
             size="modal-xl">
        {viewingStudent && (
          <div className="registration-form-preview bg-white">
            <style>
              {`
              
              .modal-dialog {
        margin-left: auto;
        margin-right: auto;
                  max-width: 1000px !important;
    }
                .modal-xl {
                  max-width: 1000px !important;
                }
                
                .registration-form-preview {
                  padding: 40px;
                  width: 100%;
                  color: #333;
                }
                
                .form-section-title {
                  background: #f8f9fa;
                  padding: 10px 15px;
                  border-left: 4px solid #7367f0;
                  margin-bottom: 25px;
                  font-weight: 700;
                  color: #333;
                  text-transform: uppercase;
                  font-size: 0.95rem;
                }

                @media print {
                  @page {
                    size: A4;
                    margin: 0;
                  }
                  
                  /* Hide EVERYTHING in the body except the modal */
                  body > *:not(.modal), 
                  .sidebar, 
                  .top-header, 
                  .navbar, 
                  .modal-header, 
                  .modal-footer, 
                  .btn-close-custom, 
                  .d-print-none {
                    display: none !important;
                    height: 0 !important;
                    overflow: hidden !important;
                  }
                  
                  /* Ensure the modal and its parents are visible */
                  #root, .app-container, .main-content, .flex-grow-1, .modal, .modal-dialog, .modal-content, .modal-body {
                    display: block !important;
                    visibility: visible !important;
                    position: static !important;
                    width: 100% !important;
                    height: auto !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    background: white !important;
                    border: none !important;
                    box-shadow: none !important;
                    transform: none !important;
                  }
                  
                  .registration-form-preview {
                    width: 210mm !important;
                    height: 297mm !important;
                    padding: 15mm !important;
                    margin: 0 auto !important;
                    background: white !important;
                    display: block !important;
                  }

                  .registration-form-preview * {
                    visibility: visible !important;
                  }
                }
              `}
            </style>
            
            <div className="registration-form-container">
              <div className="d-flex justify-content-between mb-5 pb-4 border-bottom align-items-center">
                <div className="d-flex align-items-center gap-4">
                  {settings?.app_logo ? (
                    <img 
                      src={settings.app_logo.startsWith('http') ? settings.app_logo : `https://lms.appy.pk/api${settings.app_logo.startsWith('/') ? '' : '/'}${settings.app_logo}`} 
                      alt="Logo" 
                      style={{ width: '100px', height: '100px', objectFit: 'contain' }} 
                    />
                  ) : (
                    <div className="bg-primary text-white rounded d-flex align-items-center justify-content-center shadow-sm" style={{ width: '100px', height: '100px' }}>
                      <i className="bi bi-mortarboard-fill fs-1"></i>
                    </div>
                  )}
                  <div>
                    <h1 className="fw-bold text-primary mb-1" style={{ fontSize: '2.2rem' }}>{settings?.school_name || 'Institute Name'}</h1>
                    <p className="text-muted mb-0 fw-semibold fs-5">{settings?.school_address}</p>
                    <p className="text-muted mb-0">Phone: {settings?.school_phone} | Email: {settings?.school_email}</p>
                  </div>
                </div>
                <div className="text-end">
                  <h2 className="fw-bold mb-1 text-uppercase text-primary" style={{ letterSpacing: '2px' }}>Admission Form</h2>
                  <div className="mt-2">
                    <p className="mb-0 text-muted">Reg No: <span className="fw-bold text-dark fs-4">{viewingStudent.reg_no || viewingStudent.id}</span></p>
                    <p className="text-muted mb-0">Date: <span className="fw-bold text-dark">{viewingStudent.admission_date || new Date().toLocaleDateString()}</span></p>
                  </div>
                </div>
              </div>

              <div className="row mb-5">
                <div className="col-9">
                  <div className="form-section-title">Student Personal Information</div>
                  <div className="row g-4 px-2">
                    <div className="col-6">
                      <p className="mb-0 small text-muted fw-bold">Full Name:</p>
                      <p className="fw-bold mb-0 border-bottom pb-1 fs-5">{viewingStudent.first_name} {viewingStudent.last_name}</p>
                    </div>
                    <div className="col-6">
                      <p className="mb-0 small text-muted fw-bold">Gender:</p>
                      <p className="fw-bold mb-0 border-bottom pb-1 fs-5">{viewingStudent.gender || 'N/A'}</p>
                    </div>
                    <div className="col-6">
                      <p className="mb-0 small text-muted fw-bold">Date of Birth:</p>
                      <p className="fw-bold mb-0 border-bottom pb-1 fs-5">{viewingStudent.dob || 'N/A'}</p>
                    </div>
                    <div className="col-6">
                      <p className="mb-0 small text-muted fw-bold">CNIC/B-Form:</p>
                      <p className="fw-bold mb-0 border-bottom pb-1 fs-5">{viewingStudent.cnic || 'N/A'}</p>
                    </div>
                    <div className="col-6">
                      <p className="mb-0 small text-muted fw-bold">Contact Number:</p>
                      <p className="fw-bold mb-0 border-bottom pb-1 fs-5">{viewingStudent.phone || 'N/A'}</p>
                    </div>
                    <div className="col-6">
                      <p className="mb-0 small text-muted fw-bold">Email Address:</p>
                      <p className="fw-bold mb-0 border-bottom pb-1 fs-5 text-break">{viewingStudent.email || 'N/A'}</p>
                    </div>
                  </div>
                </div>
                <div className="col-3 text-center">
                  <div className="border rounded p-1 mb-2 bg-light mx-auto shadow-sm" style={{ width: '160px', height: '180px', border: '2px dashed #7367f0 !important' }}>
                     {viewingStudent.photo ? (
                        <img 
                          src={`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}${viewingStudent.photo.startsWith('/') ? '' : '/'}${viewingStudent.photo}`} 
                          alt="Student" 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                        />
                      ) : (
                        <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center text-muted">
                          <i className="bi bi-person-bounding-box" style={{ fontSize: '3rem' }}></i>
                          <small className="mt-2 fw-bold" style={{ fontSize: '11px' }}>PHOTO HERE</small>
                        </div>
                      )}
                  </div>
                  <small className="text-muted d-block mt-2">Paste Photograph</small>
                </div>
              </div>

              <div className="mb-5">
                <div className="form-section-title">Parental & Guardian Information</div>
                <div className="row g-4 px-2">
                  <div className="col-4">
                    <p className="mb-0 small text-muted fw-bold">Father's Name:</p>
                    <p className="fw-bold mb-0 border-bottom pb-1 fs-5">{viewingStudent.father_name || 'N/A'}</p>
                  </div>
                  <div className="col-4">
                    <p className="mb-0 small text-muted fw-bold">Father's CNIC:</p>
                    <p className="fw-bold mb-0 border-bottom pb-1 fs-5">{viewingStudent.father_cnic || 'N/A'}</p>
                  </div>
                  <div className="col-4">
                    <p className="mb-0 small text-muted fw-bold">Father's Contact:</p>
                    <p className="fw-bold mb-0 border-bottom pb-1 fs-5">{viewingStudent.father_phone || 'N/A'}</p>
                  </div>
                  <div className="col-4">
                    <p className="mb-0 small text-muted fw-bold">Mother's Name:</p>
                    <p className="fw-bold mb-0 border-bottom pb-1 fs-5">{viewingStudent.mother_name || 'N/A'}</p>
                  </div>
                  <div className="col-8">
                    <p className="mb-0 small text-muted fw-bold">Residential Address:</p>
                    <p className="fw-bold mb-0 border-bottom pb-1 fs-5">{viewingStudent.address || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <div className="mb-5">
                <div className="form-section-title">Program Enrollment Details</div>
                <div className="row g-4 px-2">
                  <div className="col-4">
                    <p className="mb-0 small text-muted fw-bold">Enrolled {courseTerm}:</p>
                    <p className="fw-bold text-primary mb-0 border-bottom pb-1 fs-4">{viewingStudent.course?.name || 'N/A'}</p>
                  </div>
                  <div className="col-4">
                    <p className="mb-0 small text-muted fw-bold">Assigned {batchTerm}:</p>
                    <p className="fw-bold mb-0 border-bottom pb-1 fs-5">{viewingStudent.batch?.name || 'N/A'}</p>
                  </div>
                  <div className="col-4">
                    <p className="mb-0 small text-muted fw-bold">Admission Type:</p>
                    <p className="fw-bold mb-0 border-bottom pb-1 fs-5">Regular</p>
                  </div>
                </div>
              </div>

              <div className="row mb-5 mt-5">
                 <div className="col-5">
                  <p className="mb-2 small text-muted fw-bold text-uppercase">Form Prepared By:</p>
                  <p className="fw-bold mb-0 text-dark border-bottom pb-2 fs-5">{viewingStudent.creator?.name || 'Administrative Office'}</p>
                  <div className="mt-2">
                    <small className="text-muted">Generated: {new Date(viewingStudent.created_at).toLocaleString()}</small>
                  </div>
                </div>
                <div className="col-2"></div>
                <div className="col-5 text-center d-flex flex-column justify-content-end">
                  <div className="mb-2" style={{ borderBottom: '2px solid #333' }}></div>
                  <p className="fw-bold text-dark text-uppercase mb-0">Guardian Signature</p>
                </div>
              </div>

              <div className="bg-light p-4 rounded mb-5 text-center border" style={{ borderStyle: 'solid !important' }}>
                <h5 className="fw-bold mb-3">DECLARATION</h5>
                <p className="mb-0 text-muted lh-base px-3" style={{ fontSize: '1rem' }}>
                  "I hereby solemnly declare that all the information provided above is true and correct to the best of my knowledge. I promise to abide by the rules and regulations of <b>{settings?.school_name}</b> and understand that any false information or misconduct may result in cancellation of my admission without further notice."
                </p>
              </div>

              <div className="text-center mt-5 pt-4 border-top d-print-none">
                <button className="btn btn-primary btn-lg rounded-pill px-5 shadow-lg fw-bold py-3 mb-4" onClick={() => window.print()}>
                  <i className="bi bi-printer-fill me-2"></i> PRINT REGISTRATION FORM
                </button>
                <p className="small text-muted mb-0">System Generated | Powered by Appy LMS</p>
              </div>
            </div>
          </div>
        )}
      </Modal>
      {/* ID Card Modal */}
      {selectedIdCardStudent && (
        <Modal show={showIdCardModal} onClose={() => setShowIdCardModal(false)} title="Student ID Card" size="modal-md">
          <div className="d-flex justify-content-end mb-3">
            <button className="btn btn-primary d-print-none rounded-pill px-4" onClick={handlePrintIdCard}>
              <i className="bi bi-printer me-2"></i> Print ID Card
            </button>
          </div>
          <div className="id-card-container d-flex justify-content-center p-3">
            {(() => {
              const primaryColor = settings?.id_card_primary_color || '#4f46e5';
              const secondaryColor = settings?.id_card_secondary_color || '#ec4899';
              const gradient = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;

              return (
                <div id="id-card-print-area" className="id-card position-relative shadow" style={{ width: '2.13in', height: '3.38in', borderRadius: '12px', overflow: 'hidden', border: 'none', backgroundColor: primaryColor, fontFamily: "'Inter', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif", padding: '6px' }}>
                  
                  {/* Inner White Card */}
                  <div className="bg-white position-relative d-flex flex-column" style={{ width: '100%', height: '100%', borderRadius: '8px', overflow: 'hidden' }}>
                    
                    {/* Subtle background pattern in the white card (optional dots) */}
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, opacity: 0.04, backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '8px 8px', zIndex: 0 }}></div>

                    {/* Header: Logo and QR Code */}
                    <div className="d-flex justify-content-between align-items-center pt-2 pb-0 px-3 position-relative" style={{ zIndex: 1 }}>
                      <div className="d-flex align-items-center">
                        <img 
                          src={settings?.app_logo ? (settings.app_logo.startsWith('http') ? settings.app_logo : `${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}${settings.app_logo.startsWith('/') ? '' : '/'}${settings.app_logo}`) : "/logo.svg"} 
                          alt="Logo" 
                          style={{ height: '30px', objectFit: 'contain' }} 
                        />
                      </div>
                      <div className="bg-white p-1 rounded d-flex" style={{ border: '1px solid #e2e8f0' }}>
                        <QRCodeSVG 
                          value={`ID:${selectedIdCardStudent.reg_no || selectedIdCardStudent.id}\nName:${selectedIdCardStudent.first_name} ${selectedIdCardStudent.last_name}`} 
                          size={28} 
                          level="L" 
                        />
                      </div>
                    </div>

                    {/* Photo Container */}
                    <div className="d-flex justify-content-center mt-0 position-relative" style={{ zIndex: 1 }}>
                      {/* Ring */}
                      <div style={{ padding: '3px', backgroundColor: primaryColor, borderRadius: '50%', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
                        <div style={{ width: '75px', height: '75px', borderRadius: '50%', overflow: 'hidden', border: '2px solid #fff', backgroundColor: '#f8fafc' }}>
                          {selectedIdCardStudent.photo ? (
                            <img src={`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}${selectedIdCardStudent.photo}`} alt="Student" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div className="d-flex align-items-center justify-content-center w-100 h-100">
                              <i className="bi bi-person-fill" style={{ fontSize: '3rem', color: '#cbd5e1' }}></i>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Name & Badge */}
                    <div className="text-center mt-2 position-relative px-2" style={{ zIndex: 1 }}>
                      <h5 style={{ color: '#0f172a', fontWeight: '900', marginBottom: '3px', fontSize: '13px', letterSpacing: '-0.2px', textTransform: 'uppercase' }}>
                        {selectedIdCardStudent.first_name} {selectedIdCardStudent.last_name}
                      </h5>
                      <div className="d-inline-block px-2 py-1 rounded-pill" style={{ background: `${primaryColor}15`, color: primaryColor, fontSize: '7px', fontWeight: '800', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                        {selectedIdCardStudent.course?.name || 'STUDENT'}
                      </div>
                    </div>

                    {/* Details Grid */}
                    <div className="mt-2 px-3 position-relative flex-grow-1" style={{ zIndex: 1 }}>
                      <div className="d-flex flex-column gap-1">
                        <div className="d-flex justify-content-between align-items-center border-bottom pb-1" style={{ borderColor: '#f1f5f9' }}>
                          <span style={{ fontSize: '7px', color: '#64748b', fontWeight: '700' }}>ID NO</span>
                          <span style={{ fontSize: '8px', color: '#334155', fontWeight: '800' }}>{selectedIdCardStudent.reg_no || selectedIdCardStudent.id}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center border-bottom pb-1" style={{ borderColor: '#f1f5f9' }}>
                          <span style={{ fontSize: '7px', color: '#64748b', fontWeight: '700' }}>{courseTerm.toUpperCase()}</span>
                          <span style={{ fontSize: '8px', color: '#334155', fontWeight: '800', textTransform: 'uppercase' }}>{selectedIdCardStudent.course?.name || 'N/A'}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center border-bottom pb-1" style={{ borderColor: '#f1f5f9' }}>
                          <span style={{ fontSize: '7px', color: '#64748b', fontWeight: '700' }}>{batchTerm.toUpperCase()}</span>
                          <span style={{ fontSize: '8px', color: '#334155', fontWeight: '800', textTransform: 'uppercase' }}>{selectedIdCardStudent.batch?.name || 'N/A'}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center" style={{ borderColor: '#f1f5f9' }}>
                          <span style={{ fontSize: '7px', color: '#64748b', fontWeight: '700' }}>PHONE</span>
                          <span style={{ fontSize: '8px', color: '#334155', fontWeight: '800' }}>{selectedIdCardStudent.phone || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Bottom Section (Signature / Identity Label) */}
                    <div className="d-flex justify-content-center align-items-center p-2 mt-auto position-relative text-center" style={{ zIndex: 1, backgroundColor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                      <div>
                        <div style={{ width: '30px', height: '3px', backgroundColor: primaryColor, borderRadius: '2px', marginBottom: '4px', marginLeft: 'auto', marginRight: 'auto' }}></div>
                        <span style={{ fontSize: '6px', color: '#94a3b8', fontWeight: '800', display: 'block', letterSpacing: '0.5px' }}>AUTHORIZED IDENTITY CARD</span>
                        <span style={{ fontSize: '5.5px', color: '#cbd5e1', fontWeight: '700', display: 'block', letterSpacing: '0.2px', textTransform: 'uppercase' }}>{settings?.school_name || 'PREMIUM INSTITUTE'}</span>
                      </div>
                    </div>

                  </div>
                </div>
              );
            })()}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default StudentList;
